import { Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { portEdgeColor, portNodeColor } from '@/features/resource/components/PortGraphNode'
import type { ControlPort, MeteringPort } from '@/features/resource/types'
import type { GroupedPort } from '@/features/resource/utils/portGroups'

/*
 * Specimen ports, only ever used to colour a swatch.
 *
 * Run through `portNodeColor` rather than reading the palette directly, so the legend cannot end up
 * promising a hue the nodes do not use.
 */
const SPECIMEN_CONTROL: Omit<ControlPort, 'isOutput'> = {
  controlPortID: 'legend',
  ports: null,
  controlBoxType: null,
  category: null,
  powerMeterID: null,
  errors: null,
}

const SPECIMEN_METERING: MeteringPort = {
  powerMeterID: 'legend',
  name: null,
  controllerName: null,
  isSingleResource: null,
  errors: null,
}

const specimenControl = (isOutput: boolean | null): GroupedPort => ({
  kind: 'CONTROL',
  id: 'legend',
  name: null,
  errors: null,
  controlPort: { ...SPECIMEN_CONTROL, isOutput },
})

const SPECIMEN_METERING_PORT: GroupedPort = {
  kind: 'METERING',
  id: 'legend',
  name: null,
  errors: null,
  meteringPort: SPECIMEN_METERING,
}

function LegendRow({ swatch, children }: Readonly<{ swatch: ReactNode; children: ReactNode }>) {
  return (
    <Stack alignItems="center" direction="row" gap={1}>
      <svg aria-hidden="true" height="18" style={{ flexShrink: 0 }} viewBox="0 0 30 18" width="30">
        {swatch}
      </svg>
      <CustomTypography color="grey.500" variant="caption">
        {children}
      </CustomTypography>
    </Stack>
  )
}

export type PortGraphLegendProps = {
  /** The crossing row is the argument for the view, so it is only claimed when one is on screen. */
  hasCrossControllerEdge: boolean
  hasUnidentifiedController: boolean
}

/**
 * What the marks in the graph mean.
 *
 * Says out loud that a crossing relation is permitted. Without that the heavier, dashed line reads
 * as a warning, which is exactly what it is not.
 */
export default function PortGraphLegend({
  hasCrossControllerEdge,
  hasUnidentifiedController,
}: Readonly<PortGraphLegendProps>) {
  const { t } = useTranslation()
  const theme = useTheme()

  const inputColor = portNodeColor(specimenControl(false), theme)
  const outputColor = portNodeColor(specimenControl(true), theme)
  const unknownColor = portNodeColor(specimenControl(null), theme)
  const meteringColor = portNodeColor(SPECIMEN_METERING_PORT, theme)

  return (
    <Stack gap={1}>
      <CustomTypography color="grey.500" variant="overline">
        {t('resources.detail_panel.ports_graph_legend_title')}
      </CustomTypography>

      <LegendRow
        swatch={
          <>
            <rect
              fill={theme.palette.background.paper}
              height="14"
              rx="4"
              stroke={inputColor}
              width="27"
              x="1.5"
              y="2"
            />
            <rect fill={inputColor} height="6" rx="1.5" width="3" x="2.5" y="3.5" />
          </>
        }
      >
        {t('resources.detail_panel.ports_graph_legend_input')}
      </LegendRow>

      <LegendRow
        swatch={
          <>
            <rect
              fill={theme.palette.background.paper}
              height="14"
              rx="4"
              stroke={outputColor}
              width="27"
              x="1.5"
              y="2"
            />
            <rect fill={outputColor} height="6" rx="1.5" width="3" x="2.5" y="6" />
          </>
        }
      >
        {t('resources.detail_panel.ports_graph_legend_output')}
      </LegendRow>

      <LegendRow
        swatch={
          <>
            <rect
              fill={theme.palette.background.paper}
              height="14"
              rx="4"
              stroke={unknownColor}
              width="27"
              x="1.5"
              y="2"
            />
            <rect fill="none" height="6" rx="1.2" stroke={unknownColor} strokeWidth="0.9" width="2.4" x="3" y="4" />
          </>
        }
      >
        {t('resources.detail_panel.ports_graph_legend_direction_unknown')}
      </LegendRow>

      <LegendRow
        swatch={
          <>
            <rect
              fill={theme.palette.background.paper}
              height="14"
              rx="2"
              stroke={meteringColor}
              strokeWidth="1.6"
              width="27"
              x="1.5"
              y="2"
            />
            <rect fill={meteringColor} height="12" rx="1.75" width="3.5" x="2.5" y="3" />
          </>
        }
      >
        {t('resources.detail_panel.ports_graph_legend_metering')}
      </LegendRow>

      <LegendRow
        swatch={
          <>
            <line stroke={portEdgeColor(theme, false)} strokeWidth="1.3" x1="1" x2="24" y1="9" y2="9" />
            <path d="M 24 6 L 29 9 L 24 12 z" fill={portEdgeColor(theme, false)} />
          </>
        }
      >
        {t('resources.detail_panel.ports_graph_legend_measured_by')}
      </LegendRow>

      {hasCrossControllerEdge && (
        <LegendRow
          swatch={
            <>
              <line
                stroke={portEdgeColor(theme, true)}
                strokeDasharray="5 3"
                strokeWidth="2"
                x1="1"
                x2="24"
                y1="9"
                y2="9"
              />
              <path d="M 24 5.5 L 29 9 L 24 12.5 z" fill={portEdgeColor(theme, true)} />
            </>
          }
        >
          {t('resources.detail_panel.ports_graph_legend_cross_controller')}
        </LegendRow>
      )}

      <LegendRow
        swatch={
          <rect
            fill={theme.palette.action.hover}
            height="14"
            rx="4"
            stroke={theme.palette.divider}
            width="27"
            x="1.5"
            y="2"
          />
        }
      >
        {t('resources.detail_panel.ports_graph_legend_band')}
      </LegendRow>

      {hasUnidentifiedController && (
        <LegendRow
          swatch={
            <rect
              fill="none"
              height="14"
              rx="4"
              stroke={theme.palette.divider}
              strokeDasharray="4 3"
              width="27"
              x="1.5"
              y="2"
            />
          }
        >
          {t('resources.detail_panel.ports_graph_legend_unidentified_band')}
        </LegendRow>
      )}

      <CustomTypography color="grey.500" variant="caption">
        {t('resources.detail_panel.ports_graph_legend_hint')}
      </CustomTypography>
    </Stack>
  )
}
